import { normalizeKeyboardEvent } from '@src/services/shortcuts/keyboard';
import { LogManager } from '@src/utility/Logger';
import React, { useEffect, useMemo, useRef } from 'react';

import {
  createShortcutRegistry,
  ShortcutRegistryContext,
} from './ShortcutRegistryContext';

import type {
  ShortcutContext,
  ShortcutMatch,
  ShortcutRegistryLike,
} from '@src/services/shortcuts/types';

const logger = LogManager.getLogger('shortcuts:provider');

export interface ShortcutProviderProps {
  children: React.ReactNode;
  registry?: ShortcutRegistryLike;
}

export const ShortcutProvider: React.FC<ShortcutProviderProps> = ({
  children,
  registry: providedRegistry,
}) => {
  const internalRegistryRef = useRef<ShortcutRegistryLike | null>(null);

  const registry = useMemo(() => {
    if (providedRegistry) {
      return providedRegistry;
    }

    if (!internalRegistryRef.current) {
      internalRegistryRef.current = createShortcutRegistry();
    }

    return internalRegistryRef.current;
  }, [providedRegistry]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const normalized = normalizeKeyboardEvent(event);
      const matches = registry.resolve(normalized);

      for (const match of matches) {
        const handled = executeShortcut(match, {
          nativeEvent: normalized.nativeEvent,
          normalizedEvent: normalized,
        });

        if (handled) {
          // We defer preventDefault() until a shortcut explicitly reports it handled
          // the event so that returning false continues to allow native browser
          // behavior (e.g., typing into inputs) when a scope opts out.
          event.preventDefault();
          break;
        }
      }
    };

    // Note: We must use { passive: false } here because handled shortcuts require calling
    // event.preventDefault(). While passive listeners are preferred for scroll performance,
    // this is a keyboard event (not scroll/touch), and the tradeoff is acceptable.
    window.addEventListener('keydown', handleKeyDown, { passive: false });
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [registry]);

  return (
    <ShortcutRegistryContext.Provider value={registry}>
      {children}
    </ShortcutRegistryContext.Provider>
  );
};

function executeShortcut(
  match: ShortcutMatch,
  context: ShortcutContext,
): boolean {
  try {
    const result = match.definition.run(context);

    if (isPromise(result)) {
      result.catch(error =>
        logger.error('shortcut handler rejected', {
          error,
          shortcutId: match.definition.id,
          scopeId: match.scopeId,
        }),
      );
      return true;
    }

    return result !== false;
  } catch (error) {
    logger.error('shortcut handler threw', {
      error,
      shortcutId: match.definition.id,
      scopeId: match.scopeId,
    });
    return true;
  }
}

function isPromise(value: unknown): value is Promise<unknown> {
  return value instanceof Promise;
}
